/**
 * Wire types for the `expenses` module — derived field-by-field from
 * `serializeExpense` (refactor/backend/src/modules/expenses/serializers.ts).
 * snake_case verbatim, no camelCase mapping layer — see the FIELD-NAMING
 * DECISION note in src/types/user.ts.
 *
 * `amount` is `Decimal(10, 2)` here (NOT the `(12, 2)` payments module uses),
 * but on the main list/detail endpoints it is still a quantised STRING like
 * payments' money fields. `/summary/`'s `total` is the one exception — a raw
 * JSON number, because it comes off a plain aggregation view, not a
 * `DecimalField`. Do not conflate the two within this module.
 */

/** Django `expenses.models.ExpenseCategory`. */
export const EXPENSE_CATEGORIES = [
  'MAINTENANCE',
  'UTILITIES',
  'INSURANCE',
  'TAXES',
  'REPAIRS',
  'MANAGEMENT_FEES',
  'OTHER',
] as const;
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

/** `ExpenseSerializer` — backend serializers.ts:30-50. */
export interface Expense {
  id: number;
  property: number;
  property_name: string;
  category: ExpenseCategory;
  amount: string;
  date: string;
  description: string;
  receipt: string | null;
  vendor: string;
  created_at: string;
  updated_at: string;
}

/**
 * `GET expenses/summary/` row — a BARE ARRAY of these, not paginated. `total`
 * is a raw JSON number here, unlike every money field on `Expense` above.
 */
export interface ExpenseSummaryRow {
  category: ExpenseCategory;
  total: number;
}

// ---------------------------------------------------------------------------
// Write requests
// ---------------------------------------------------------------------------

/**
 * `expenseCreateSchema` on write (serializers.ts:63-71).
 * `read_only_fields = ['id', 'created_at', 'updated_at']`.
 */
export interface ExpenseCreateRequest {
  property: number;
  category?: ExpenseCategory | undefined;
  amount: number | string;
  date: string;
  description: string;
  receipt?: string | null | undefined;
  vendor?: string | undefined;
}

/** `expensePatchSchema` — every field of the create request, optional. */
export type ExpensePatchRequest = Partial<ExpenseCreateRequest>;
